"use client";

import { useState } from "react";
import ProductCard from "./ProductCard";
import { useLanguage } from "@/context/LanguageContext";

export default function ProductsSection() {
  const [activeCategory, setActiveCategory] = useState("all");
  const { t } = useLanguage();

  const categories = [
    { id: "cat_all", value: "all", labelKey: "prod_cat_all" as const },
    { id: "cat_carpets", value: "carpets", labelKey: "prod_cat_carpets" as const },
    { id: "cat_souvenirs", value: "souvenirs", labelKey: "prod_cat_souvenirs" as const },
    { id: "cat_decor", value: "decor", labelKey: "prod_cat_decor" as const },
    { id: "cat_gifts", value: "gifts", labelKey: "prod_cat_gifts" as const },
  ];

  const products = [
    {
      id: "prod_001",
      name: "Traditional Berber Carpet - Red & Gold Geometric",
      category: "carpets",
      price: 850,
      currency: "TND",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12532c850-1767719242746.png",
      alt: "Handwoven Berber carpet with red and gold geometric patterns on cream background",
      rating: 5,
      reviews: 47
    },
    {
      id: "prod_002",
      name: "Hand-Painted Ceramic Bowl Set (6 pieces)",
      category: "souvenirs",
      price: 250,
      currency: "TND",
      image: "https://images.unsplash.com/photo-1567693016361-8be252b3848d",
      alt: "Set of six hand-painted ceramic bowls with traditional blue and white Tunisian patterns",
      rating: 4.5,
      reviews: 32
    },
    {
      id: "prod_003",
      name: "Brass Lantern with Colored Glass Panels",
      category: "decor",
      price: 420,
      currency: "TND",
      image: "https://images.unsplash.com/photo-1599653675774-d15cee6b7ada",
      alt: "Ornate brass lantern with multi-colored stained glass panels in traditional Moroccan style",
      rating: 5,
      reviews: 28
    },
    {
      id: "prod_004",
      name: "Olive Wood Serving Board with Handles",
      category: "gifts",
      price: 185,
      currency: "TND",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1538a4691-1767192406508.png",
      alt: "Handcrafted olive wood serving board with natural grain pattern and curved handles",
      rating: 4.5,
      reviews: 41
    },
    {
      id: "prod_005",
      name: "Kilim Wool Carpet - Blue & Ivory Pattern",
      category: "carpets",
      price: 1080,
      currency: "TND",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a8ac8ec8-1764657548363.png",
      alt: "Traditional kilim wool carpet with intricate blue and ivory geometric patterns",
      rating: 5,
      reviews: 35
    },
    {
      id: "prod_006",
      name: "Handwoven Basket Set with Leather Handles",
      category: "decor",
      price: 340,
      currency: "TND",
      image: "https://images.unsplash.com/photo-1627202626612-1e304a201b32",
      alt: "Set of three handwoven natural fiber baskets with brown leather handles in graduated sizes",
      rating: 4.5,
      reviews: 26
    },
    {
      id: "prod_007",
      name: "Tagine Cooking Pot - Traditional Design",
      category: "souvenirs",
      price: 270,
      currency: "TND",
      image: "https://images.unsplash.com/photo-1706793986649-5c6516491489",
      alt: "Authentic ceramic tagine pot with conical lid featuring traditional Tunisian painted designs",
      rating: 5,
      reviews: 52
    },
    {
      id: "prod_008",
      name: "Silver Filigree Jewelry Box with Mirror",
      category: "gifts",
      price: 500,
      currency: "TND",
      image: "https://images.unsplash.com/photo-1587237776892-d2aed556451b",
      alt: "Ornate silver filigree jewelry box with intricate patterns and interior mirror",
      rating: 5,
      reviews: 19
    }
  ];

  const filteredProducts =
    activeCategory === "all"
      ? products
      : products?.filter((p) => p?.category === activeCategory);

  return (
    <section id="products" className="py-20 md:py-32 bg-background">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12 reveal will-animate">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">
            {t("prod_section_badge")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mt-4 mb-6">
            {t("prod_section_title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("prod_section_desc")}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 reveal will-animate">
          {categories?.map((cat) => (
            <button
              key={cat?.id}
              onClick={() => setActiveCategory(cat?.value)}
              className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                activeCategory === cat?.value
                  ? "bg-primary text-white shadow-lg scale-105"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredProducts?.map((product) => (
            <ProductCard key={product?.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}